import {injectable} from "inversify";
import SceneUtils from "@/utils/scene-utils";
import {Listener} from "@/store/listener";
import ListenerManager from "@/managers/listener-manager";
import PlayerStore from "@/store/player";
import ShopStore from "@/store/shop";
import {PaymentPanel} from "@/scenes.ui/payment-panel";
import {ChestType, Player} from "@/store/player/types";
import {PurchaseInfo} from "@/store/shop/types";
import {StatusAcquiredPanel} from "@/scenes.ui/status-acquired-panel";
import {BonusPointsPanel} from "@/scenes.ui/bonus-points-panel";
import {IapService} from "@/services/iap-service";
import {AppmetricaService} from "@/services/appmetrica-service";
import {NewCreaturePanel} from "@/scenes.ui/new-creature-panel";
import {PurchasePanel} from "@/scenes.ui.offer/purchase-panel";
import BaseSound = Phaser.Sound.BaseSound;
import { GiftNotificationPanel } from '@/scenes.ui/gift-notification-panel';

@injectable()
export class PaymentNotificationService implements Listener {

    private static readonly REFRESH_INTERVAL: number = 5000;

    private timerId: number | undefined = undefined;

    private showManual!: () => unknown;

    private appmetricaService!: AppmetricaService | null;

    private completeSound!: BaseSound;
    private clickSound!: BaseSound;

    public init(showManual: () => unknown, appmetricaService: AppmetricaService | null = null,
                completeSound: BaseSound, clickSound: BaseSound) {
        this.completeSound = completeSound;
        this.clickSound = clickSound;

        if (appmetricaService != null) {
            this.appmetricaService = appmetricaService;
        }

        this.showManual = showManual;
        ListenerManager.addPlayerListener(this);
        this.onChange();
    }

    onChange(): void {
        this.checkPayments();
    }

    public checkPayments(): void {
        let p: Player = PlayerStore.getPlayer();
        if (p.havePurchases) {
            this.job();
        }
    }

    public setupPaymentCheckingTask() {
        ListenerManager.setOkGemsCallback(() => {
            this.job();
        });

        if (!this.timerId) {
            this.timerId = setInterval(() => {
                this.job();
            }, PaymentNotificationService.REFRESH_INTERVAL);
        }
    }

    private job() {
        let self = this;
        ShopStore.getPurchaseInfo().then((pi: PurchaseInfo) => {
            self.checkPayment(pi);
        });
    }

    private static panelOpened = false;

    public checkPayment(pi: PurchaseInfo) {
        if (PaymentNotificationService.panelOpened) {
            return;
        }
        if (!pi) {
            return;
        }
        let cnt = (pi.items ? pi.items.length : 0) + (pi.creatures ? pi.creatures.length : 0);
        if (cnt <= 0) {
            return;
        }

        ListenerManager.callPurchaseRecievedCallbacl();
        let scene = SceneUtils.getCurrentScene();
        let canvas = SceneUtils.getCanvas();
        let self = this;

        let takeGemsFunc = () => {
            ShopStore.takePaidGems().then((player: Player) => {
                let p: Player = PlayerStore.getPlayer();
                p.havePurchases = false;
                if (player.chestType && (player.chestType === ChestType.GOLD || player.chestType === ChestType.SILVER
                        || player.chestType === ChestType.AZURE || player.chestType === ChestType.LAVA
                        || player.chestType === ChestType.AMBER || player.chestType === ChestType.JADE)) {
                    ListenerManager.callOpenChestPanelCallback();
                }

                if (!IapService.IapEnabled() &&
                    process.env.VUE_APP_IS_MOBILE_APP === 'true' &&
                    this.appmetricaService != null) {
                    this.appmetricaService.reportEvent(AppmetricaService.TRANSACTION_EVENT, {
                        "paidGemsToTake": pi.paidGemsToTake
                    });
                    console.log("PurchaseStore: Success Appmetrica transaction event")
                }
            });
        };

        let showManual = () => {
            self.showManual();
        };

        let ad: PurchasePanel = new PurchasePanel(scene, canvas, pi, showManual, self.completeSound!, self.clickSound!);

        ad.setCloseListener(() => {
            PaymentNotificationService.panelOpened = false;
            let newStatusFunc = () => {
                if (pi.statusAfterPurchase !== pi.currentStatus) {
                    let sap: StatusAcquiredPanel = new StatusAcquiredPanel(scene, canvas, pi, showManual);
                    sap.openAnimated();
                    sap.setCloseListener(() => {
                        bonusPointsFunc();
                    });
                } else {
                    bonusPointsFunc();
                }
            };
            let bonusPointsFunc = () => {
                if (pi.pointsToTake > 0) {
                    let bpp: BonusPointsPanel = new BonusPointsPanel(scene, canvas, pi, showManual);
                    bpp.openAnimated();
                    bpp.setCloseListener(() => {
                        takeGemsFunc();
                    });
                } else {
                    takeGemsFunc();
                }
            };
            newStatusFunc();
        });

        // Stopping scheduled job
        if (this.timerId) {
            clearInterval(this.timerId);
        }

        this.timerId = undefined;
        let p: Player = PlayerStore.getPlayer();
        p.havePurchases = false;
        PaymentNotificationService.panelOpened = true;

        if (pi.items.find(item => item.giftSenderName != undefined)) {
            const senderNames = pi.items.filter(item => !!item.giftSenderName).map(item => item.giftSenderName!);
            const giftNotificationPanel = new GiftNotificationPanel(scene, canvas, senderNames, () => ad.open());
            giftNotificationPanel.open();
        } else {
            ad.open();
        }
    }
}
